import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Keyboard,
  TextInput,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollView,
} from "react-native";

import type { UiTheme } from "../theme";

// Sibling of Logger/LoggerRevealScroller.tsx, kept apart on purpose: this one
// clears the keyboard's top edge, that one clears the logger's absolute dock.
// Keeps the field being typed into above the keyboard (#336 device smoke:
// the reps field of an open round sat under it and the page did not move).
// Scrolls only by the covered amount plus room for the next field of the
// chain, only while the keyboard is up, and never when the field is clear.
// Two triggers, as the logger's reveal learned: the field's focus (the keyboard
// is already up when "next" walks to the following field) and the keyboard
// showing (at the first focus it has not come up yet).

// A beat for the layout (keyboard padding, focus) to settle before measuring.
const SETTLE_MS = 50;

type Reveal = () => void;
const RevealContext = createContext<Reveal>(() => {});

/** Leaf side: call on a field's focus. */
export function useRevealFocused(): Reveal {
  return useContext(RevealContext);
}

export const RevealFocusedProvider = RevealContext.Provider;

export function useRevealFocusedField(theme: UiTheme) {
  const scrollRef = useRef<ScrollView>(null);
  const offset = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keyboardTop = useRef<number | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  // Room under the field: the next field of the chain stays in view too.
  const room = theme.spacing.s8 + theme.spacing.s5;

  const reveal = useCallback<Reveal>(() => {
    const input = TextInput.State?.currentlyFocusedInput?.();
    const top = keyboardTop.current;
    const scroller = scrollRef.current;
    if (!input || top === null || !scroller) return;
    input.measureInWindow((_x, y, _w, h) => {
      const covered = y + h + room - top;
      if (covered > 0) scroller.scrollTo({ y: offset.current + covered, animated: true });
    });
  }, [room]);

  const later = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(reveal, SETTLE_MS);
  }, [reveal]);

  useEffect(() => {
    const show = Keyboard.addListener("keyboardDidShow", (e) => {
      keyboardTop.current = e.endCoordinates.screenY;
      setKeyboardHeight(e.endCoordinates.height);
      // After the padding below has laid out, so the scroll range exists.
      later();
    });
    const hide = Keyboard.addListener("keyboardDidHide", () => {
      keyboardTop.current = null;
      setKeyboardHeight(0);
    });
    return () => {
      show.remove();
      hide.remove();
      if (timer.current) clearTimeout(timer.current);
    };
  }, [later]);

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    offset.current = e.nativeEvent.contentOffset.y;
  }, []);

  // Focus moved with the keyboard already up: give the field a beat to settle.
  const onFieldFocus = later;

  return { scrollRef, onScroll, keyboardHeight, onFieldFocus };
}
